<!--
  HandOffOwnershipDialog -- ADR 0021 "Hand off ownership" shortcut.

  Card: Distributor Onboarding & Account (https://trello.com/c/QV2U4KhF)
  Pebble: Account section pages -- Team & Permissions and Payments
  (replacing placeholder frames).

  ADR 0021 (Owner offboarding, smooth case): a single-button workflow on
  Team & Permissions that promotes a current Builder to Owner and demotes
  (or removes) the departing Owner in one atomic action. This component is
  the modal that confirmation runs through. It owns NO data layer: it takes
  the list of eligible recipients as a prop and emits `transfer-ownership`
  with the chosen recipient + the "remove me entirely" choice. The page's
  useTeamSubscription composable performs the POST.

  The recipient set is "any current Builder" -- the caller filters the
  account list down to active, non-Owner, non-invited teammates before
  passing it in. An empty set disables the confirm affordance (you cannot
  hand off to nobody).
-->
<template>
  <v-dialog v-model="showDialog" persistent max-width="500px">
    <v-card data-testid="handoff-ownership-dialog">
      <v-card-title>
        <div class="d-flex align-center">
          <h1>Hand Off Ownership</h1>
          <v-spacer />
          <v-btn icon variant="plain" @click="handleCancel">
            <v-icon icon="close" />
          </v-btn>
        </div>
      </v-card-title>
      <v-divider />
      <v-card-text>
        <div class="text-h6 mb-3">
          Promote a teammate to Owner. They gain full account control. You
          stay on the account as a Builder unless you choose to remove
          yourself entirely.
        </div>

        <div v-if="recipients.length === 0" data-testid="handoff-no-recipients">
          There are no eligible teammates to promote. Invite a Builder
          first, then hand off ownership to them.
        </div>

        <v-form v-else v-model="valid">
          <v-text-field
            label="Promote teammate to Owner"
            v-model="selectedEmail"
            data-testid="handoff-recipient-select"
            :rules="[validateNotEmpty]"
          />
        </v-form>
      </v-card-text>
      <v-card-actions class="py-3 px-3">
        <v-btn variant="flat" @click="handleCancel">Go back</v-btn>
        <v-spacer />
        <v-btn
          variant="flat"
          color="primary"
          class="text-white"
          data-testid="confirm-handoff-ownership"
          :disabled="recipients.length === 0 || !selectedEmail"
          @click="handleConfirm"
        >
          Hand off ownership
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import helpers from "@/mixins/helpers";

export default {
  name: "HandOffOwnershipDialog",

  mixins: [helpers],

  props: {
    // Eligible recipients (active Builders). The page filters the account
    // list before passing it in; this component does not reinvent that.
    recipients: {
      type: Array,
      default: () => [],
    },
  },

  emits: ["transfer-ownership"],

  data() {
    return {
      showDialog: false,
      valid: null,
      selectedEmail: null,
    };
  },

  methods: {
    validateNotEmpty(value) {
      return !!value || "Required";
    },

    show() {
      this.selectedEmail = null;
      this.showDialog = true;
    },

    handleCancel() {
      this.showDialog = false;
    },

    handleConfirm() {
      const recipient = this.recipients.find(
        (r) => r.email === this.selectedEmail
      ) || { email: this.selectedEmail };
      this.$emit("transfer-ownership", recipient);
      this.showDialog = false;
    },
  },
};
</script>
<style scoped></style>
